import React, { useState } from "react";
import { Ticket } from "../../../../../models/Ticket";
import EAModal from "../../../../shared/Modal/EAModal";
import { useTranslation } from "react-i18next";
import {
  getDisplayTicketType,
  getFormattedValidFromDate,
  getFormattedValidToDate,
  getTicketStatusSummary,
  getUsageSectionData,
  shouldShowReservations,
} from "../../../../../utils/TicketPassHelper";
import "./PassModal.scss";
import DetailsGrid from "./DetailsGrid";
import DetailsHeader from "./DetailsHeader";
import DetailsQR from "./DetailsQR";
import DetailsExercises from "./DetailsExercises";
import DetailsUsageProgress from "./DetailsUsageProgress";
import SwimmingCourseModal from "./SwimmingCourseModal";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAAlert from "../../../../shared/EAAlert";
import useBasketManager from "../../../../FinalizePage/hooks/useBasketManager";
import { useNavigate } from "react-router-dom";
import { FINALIZE_PAGE } from "../../../../../routes";
import { useOtherAccountDisplayName } from "../../../../../../utils/hooks/useOtherAccountDisplayName";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  pass: Ticket;
  onRefreshTickets?: () => void;
  tempNotice?: string; // TEMP_PASS_NOTICES
}

const PassDetailsModal: React.FC<IProps> = ({ isOpen, onClose, pass, onRefreshTickets, tempNotice }) => {
  const { t } = useTranslation();
  const status = getTicketStatusSummary(pass);
  const { prolongPass } = useBasketManager();
  const navigate = useNavigate();
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [extendError, setExtendError] = useState("");
  const otherAccountName = useOtherAccountDisplayName(pass.accountIds);

  // Sekcja "Wykorzystanie" pokazuje już datę ważności (i ew. liczbę wejść) -
  // gdy się wyświetla, w gridzie nie duplikujemy tych informacji
  const usage = getUsageSectionData(pass);
  const showValidityDatesInGrid = usage.mode === "none";

  // Mapa z danymi do wyświetlenia w gridzie
  const detailsData = [
    {
      key: "numer_karnetu",
      value: pass.trhId || "-",
    },
    {
      key: "typ",
      value: getDisplayTicketType(pass),
    },
    ...(showValidityDatesInGrid
      ? [
          {
            key: "wazny_od",
            value: getFormattedValidFromDate(pass),
          },
          {
            key: "wazny_do",
            value: getFormattedValidToDate(pass),
          },
        ]
      : []),
    ...(otherAccountName
      ? [{ key: "Uczestnik", value: otherAccountName }]
      : []),
  ];

  const handleExtendPass = async () => {
    setExtendError("");
    const newValidTo = new Date(pass.validto);
    newValidTo.setDate(newValidTo.getDate() + 1);
    const result = await prolongPass(pass.assortIds, 1, newValidTo);
    if (!result?.ok) {
      setExtendError(result?.error ?? "");
      return;
    }
    onClose();
    setTimeout(() => {
      navigate(FINALIZE_PAGE);
    }, 600);
  };

  return (
    <>
      <EAModal isOpen={isOpen} title={t("szczegoly_karnetu")} onClose={onClose} hideButtons desktopWidth="600px">
        <div className="ticket-details">
          {/* SEKCJA 1: Nazwa karnetu + Status */}
          <DetailsHeader pass={pass} />

          {/* SEKCJA 2: Szczegóły karnetu - 2 kolumny */}
          <DetailsGrid detailsData={detailsData} />

          {/* SEKCJA: Wykorzystanie - uniwersalna sekcja z paskiem postępu (wejścia lub ważność) */}
          <DetailsUsageProgress pass={pass} />

          {/* TEMP_PASS_NOTICES - uwaga do karnetu */}
          {/* {tempNotice && (
            <EAAlert type="error" message={tempNotice} compact={false} variant="chip" />
          )} */}

          {/* SEKCJA 3: Alert - jeśli karnet wymaga akcji */}
          {status.message && (
            <EAAlert
              type={status.color === "error" ? "error" : "warning"}
              message={status.message}
              compact={false}
              variant="chip"
            />
          )}

          {/* SEKCJA 4: Postęp kursu szkolki pływackiej */}
          {/* {isSwimmingSchool(pass.passtype) && (
            <DetailsSwimmingProgress pass={pass} onOpenCourseModal={() => setIsCourseModalOpen(true)} />
          )} */}

          {/* SEKCJA 5: QR Code */}
          <DetailsQR pass={pass} />

          {/* SEKCJA 5: Rezerwacje — tylko gdy produkt wymaga zapisu na termin */}
          {shouldShowReservations(pass) && <DetailsExercises pass={pass} onRefreshTickets={onRefreshTickets} />}

          {/* SEKCJA 6: Przyciski */}
          <div className="ticket-details__actions">
            {extendError && <EAAlert type="error" message={extendError} />}
            {pass.extendAllowed && (
              <EAButton title={t("przedluz_karnet")} variant="filled" onClick={handleExtendPass} fullWidth />
            )}
            <EAButton title={t("zamknij")} variant="outlined" onClick={onClose} fullWidth />
          </div>
        </div>
      </EAModal>

      <SwimmingCourseModal isOpen={isCourseModalOpen} onClose={() => setIsCourseModalOpen(false)} pass={pass} />
    </>
  );
};

export default PassDetailsModal;
